import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  LocalOffer as LocalOfferIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const defaultFormData = {
  code: "",
  name: "",
  description: "",
  type: "percentage",
  value: "",
  minOrder: "",
  maxDiscount: "",
  maxUses: "",
  perUserLimit: "1",
  startAt: "",
  endAt: "",
  active: true,
};

function normalizeDateTimeInput(value) {
  if (!value) return "";

  const parsed =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const hours = String(parsed.getHours()).padStart(2, "0");
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function asNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatCurrency(value) {
  return `A$${asNumber(value, 0).toFixed(2)}`;
}

function formatDateDisplay(value) {
  if (!value) return "Not set";

  const parsed =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not set";

  return parsed.toLocaleString();
}

function getCouponStatus(coupon) {
  const now = Date.now();
  const startAt = coupon?.startAt?.toDate
    ? coupon.startAt.toDate().getTime()
    : coupon?.startAt
    ? new Date(coupon.startAt).getTime()
    : null;
  const endAt = coupon?.endAt?.toDate
    ? coupon.endAt.toDate().getTime()
    : coupon?.endAt
    ? new Date(coupon.endAt).getTime()
    : null;
  const usedCount = asNumber(coupon?.usedCount, 0);
  const maxUses = asNumber(coupon?.maxUses, 0);

  if (coupon?.active === false || coupon?.status === "inactive") {
    return { label: "Inactive", color: "default" };
  }

  if (startAt && now < startAt) {
    return { label: "Scheduled", color: "info" };
  }

  if (endAt && now > endAt) {
    return { label: "Expired", color: "warning" };
  }

  if (maxUses > 0 && usedCount >= maxUses) {
    return { label: "Limit Reached", color: "warning" };
  }

  return { label: "Active", color: "success" };
}

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [formData, setFormData] = useState(defaultFormData);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      setError("");

      let snapshot;
      try {
        snapshot = await getDocs(
          query(collection(db, "coupons"), orderBy("createdAt", "desc"))
        );
      } catch (queryError) {
        console.warn(
          "Falling back to unordered coupon fetch:",
          queryError?.message || queryError
        );
        snapshot = await getDocs(collection(db, "coupons"));
      }

      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      rows.sort((a, b) => {
        const aTime =
          typeof a?.createdAt?.toMillis === "function"
            ? a.createdAt.toMillis()
            : new Date(a?.createdAt || 0).getTime();
        const bTime =
          typeof b?.createdAt?.toMillis === "function"
            ? b.createdAt.toMillis()
            : new Date(b?.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setCoupons(rows);
    } catch (err) {
      console.error("Error fetching coupons:", err);
      setError("Failed to load coupons. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const filteredCoupons = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return coupons;

    return coupons.filter((coupon) => {
      const haystack = [
        coupon.code,
        coupon.name,
        coupon.description,
        coupon.type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalized);
    });
  }, [coupons, searchTerm]);

  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingCouponId(null);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditCoupon = (coupon) => {
    setEditingCouponId(coupon.id);
    setFormData({
      code: coupon.code || coupon.id || "",
      name: coupon.name || "",
      description: coupon.description || "",
      type: coupon.type || coupon.discountType || "percentage",
      value: String(coupon.value ?? coupon.discountValue ?? ""),
      minOrder: String(coupon.minOrder ?? coupon.minimumOrder ?? ""),
      maxDiscount: String(coupon.maxDiscount ?? coupon.maximumDiscount ?? ""),
      maxUses: String(coupon.maxUses ?? coupon.usageLimit ?? ""),
      perUserLimit: String(coupon.perUserLimit ?? coupon.maxUsesPerUser ?? "1"),
      startAt: normalizeDateTimeInput(coupon.startAt || coupon.validFrom),
      endAt: normalizeDateTimeInput(coupon.endAt || coupon.validUntil),
      active: coupon.active !== false && coupon.status !== "inactive",
    });
  };

  const handleSubmit = async () => {
    const normalizedCode = String(formData.code || "")
      .trim()
      .toUpperCase();

    if (!normalizedCode) {
      showSnackbar("Coupon code is required.", "error");
      return;
    }

    const type = formData.type === "fixed" ? "fixed" : "percentage";
    const value = asNumber(formData.value, NaN);
    if (!Number.isFinite(value) || value <= 0) {
      showSnackbar("Coupon value must be greater than 0.", "error");
      return;
    }

    if (type === "percentage" && value > 100) {
      showSnackbar("Percentage coupons cannot exceed 100%.", "error");
      return;
    }

    const startAt = formData.startAt ? new Date(formData.startAt) : null;
    const endAt = formData.endAt ? new Date(formData.endAt) : null;
    if (startAt && endAt && startAt >= endAt) {
      showSnackbar("End date must be after the start date.", "error");
      return;
    }

    try {
      setSaving(true);

      const targetId = editingCouponId || normalizedCode;
      const couponRef = doc(db, "coupons", targetId);
      const existingSnap = await getDoc(couponRef);

      if (!editingCouponId && existingSnap.exists()) {
        showSnackbar("A coupon with this code already exists.", "error");
        return;
      }

      const existingData = existingSnap.exists() ? existingSnap.data() : {};
      const payload = {
        code: normalizedCode,
        codeLower: normalizedCode.toLowerCase(),
        name: String(formData.name || "").trim(),
        description: String(formData.description || "").trim(),
        type,
        value,
        minOrder: Math.max(0, asNumber(formData.minOrder, 0)),
        maxDiscount:
          type === "percentage" ? Math.max(0, asNumber(formData.maxDiscount, 0)) : 0,
        maxUses: Math.max(0, asNumber(formData.maxUses, 0)),
        perUserLimit: Math.max(0, asNumber(formData.perUserLimit, 0)),
        usedCount: asNumber(existingData.usedCount, 0),
        usageByUser:
          existingData.usageByUser ||
          existingData.usedByUser ||
          existingData.userUsage ||
          {},
        active: Boolean(formData.active),
        status: formData.active ? "active" : "inactive",
        startAt,
        endAt,
        updatedAt: serverTimestamp(),
      };

      if (!existingSnap.exists()) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(couponRef, payload, { merge: true });

      showSnackbar(
        editingCouponId
          ? "Coupon updated successfully."
          : "Coupon created successfully."
      );
      resetForm();
      await fetchCoupons();
    } catch (err) {
      console.error("Error saving coupon:", err);
      showSnackbar("Failed to save coupon.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (coupon) => {
    try {
      await updateDoc(doc(db, "coupons", coupon.id), {
        active: !(coupon.active !== false),
        status: coupon.active !== false ? "inactive" : "active",
        updatedAt: serverTimestamp(),
      });

      showSnackbar(
        coupon.active !== false
          ? "Coupon deactivated successfully."
          : "Coupon activated successfully."
      );
      await fetchCoupons();
    } catch (err) {
      console.error("Error updating coupon status:", err);
      showSnackbar("Failed to update coupon status.", "error");
    }
  };

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6">
                    {editingCouponId ? "Edit Coupon" : "Create Coupon"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    One global coupon per order, validated on the server.
                  </Typography>
                </Box>
                <LocalOfferIcon color="primary" />
              </Stack>

              <Stack spacing={2}>
                <TextField
                  label="Coupon Code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="SAVE10"
                  disabled={Boolean(editingCouponId)}
                  helperText={
                    editingCouponId
                      ? "Coupon code is locked after creation."
                      : "Codes are stored in uppercase."
                  }
                />

                <TextField
                  label="Internal Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Autumn campaign"
                />

                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  minRows={3}
                  placeholder="Optional note for admins"
                />

                <TextField
                  select
                  label="Coupon Type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </TextField>

                <TextField
                  label={formData.type === "percentage" ? "Value (%)" : "Value (A$)"}
                  name="value"
                  type="number"
                  value={formData.value}
                  onChange={handleInputChange}
                />

                <TextField
                  label="Minimum Service Total (A$)"
                  name="minOrder"
                  type="number"
                  value={formData.minOrder}
                  onChange={handleInputChange}
                />

                <TextField
                  label="Max Discount (A$)"
                  name="maxDiscount"
                  type="number"
                  value={formData.maxDiscount}
                  onChange={handleInputChange}
                  disabled={formData.type !== "percentage"}
                  helperText={
                    formData.type === "percentage"
                      ? "Leave 0 for no cap."
                      : "Only used for percentage coupons."
                  }
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Max Uses"
                      name="maxUses"
                      type="number"
                      value={formData.maxUses}
                      onChange={handleInputChange}
                      helperText="Leave 0 for unlimited."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Per User Limit"
                      name="perUserLimit"
                      type="number"
                      value={formData.perUserLimit}
                      onChange={handleInputChange}
                      helperText="Leave 0 for unlimited."
                    />
                  </Grid>
                </Grid>

                <TextField
                  label="Start Date"
                  name="startAt"
                  type="datetime-local"
                  value={formData.startAt}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="End Date"
                  name="endAt"
                  type="datetime-local"
                  value={formData.endAt}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      name="active"
                      onChange={handleInputChange}
                    />
                  }
                  label="Coupon is active"
                />

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={editingCouponId ? <SaveIcon /> : <AddIcon />}
                    onClick={handleSubmit}
                    disabled={saving}
                  >
                    {editingCouponId ? "Update Coupon" : "Create Coupon"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={resetForm}
                    disabled={saving}
                  >
                    Reset
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography variant="h6">Coupon Library</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active coupons are checked in Cloud Functions before Stripe checkout.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    placeholder="Search coupons"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchCoupons}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {loading ? (
            <Typography>Loading coupons...</Typography>
          ) : filteredCoupons.length === 0 ? (
            <Typography color="text.secondary">No coupons found.</Typography>
          ) : (
            <Grid container spacing={2}>
              {filteredCoupons.map((coupon) => {
                const status = getCouponStatus(coupon);
                const valueLabel =
                  coupon.type === "percentage"
                    ? `${asNumber(coupon.value, 0)}%`
                    : formatCurrency(coupon.value);

                return (
                  <Grid item xs={12} md={6} key={coupon.id}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <Box>
                            <Typography variant="h6">{coupon.code || coupon.id}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {coupon.name || "Untitled coupon"}
                            </Typography>
                          </Box>
                          <Chip label={status.label} color={status.color} size="small" />
                        </Stack>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1.5, minHeight: 40 }}
                        >
                          {coupon.description || "No description provided."}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Stack spacing={0.75}>
                          <Typography variant="body2">
                            <strong>Discount:</strong> {valueLabel}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Minimum service total:</strong>{" "}
                            {formatCurrency(coupon.minOrder)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Max discount:</strong>{" "}
                            {coupon.type === "percentage"
                              ? formatCurrency(coupon.maxDiscount)
                              : "Not used"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Usage:</strong> {asNumber(coupon.usedCount, 0)}
                            {asNumber(coupon.maxUses, 0) > 0
                              ? ` / ${asNumber(coupon.maxUses, 0)}`
                              : " / unlimited"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Per user:</strong>{" "}
                            {asNumber(coupon.perUserLimit, 0) > 0
                              ? asNumber(coupon.perUserLimit, 0)
                              : "Unlimited"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Starts:</strong> {formatDateDisplay(coupon.startAt)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Ends:</strong> {formatDateDisplay(coupon.endAt)}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditCoupon(coupon)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleToggleStatus(coupon)}
                          >
                            {coupon.active !== false ? "Deactivate" : "Activate"}
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Coupons;
